import React, { useMemo } from 'react';
import type { TravelerInformationResponseArgs } from '@this/domain/traveler_information';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { ReservationInfo } from '../api/reservationInfo';
import TravelerInfoDisplay, { TRAVELER_DISPLAY_TYPE } from './TravelerInfoDisplay';
import {
  PREFERRED_TICKETING_METHOD_LABELS,
  SHINKANSEN_ADDRESS_TYPE_LABELS
} from './message_section/ReservationInfoFormTypes';

interface Props {
  hotelTravelerInfo: TravelerInformationResponseArgs | null;
  flightTravelerInfo: TravelerInformationResponseArgs | null;
  shinkansenTravelerInfo: TravelerInformationResponseArgs | null;
  hasHotelReservation: boolean;
  hasFlightTransport: boolean;
  hasShinkansenTransport: boolean;
  shinkansenInfo: ReservationInfo['shinkansenInfo'];
}

const ReservationInfoSection = ({
  hotelTravelerInfo,
  flightTravelerInfo,
  shinkansenTravelerInfo,
  hasHotelReservation,
  hasFlightTransport,
  hasShinkansenTransport,
  shinkansenInfo
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const shinkansenTicketingSummary = useMemo(() => {
    if (!hasShinkansenTransport || !shinkansenInfo) return null;
    const { preferredTicketingMethod, shinkansenAddressType, shinkansenAddress, shinkansenAddressee, postcode } =
      shinkansenInfo.currentValues;
    const methodLabel = preferredTicketingMethod
      ? PREFERRED_TICKETING_METHOD_LABELS[preferredTicketingMethod]
      : null;
    const addressTypeLabel = shinkansenAddressType ? SHINKANSEN_ADDRESS_TYPE_LABELS[shinkansenAddressType] : null;
    const isPostal = preferredTicketingMethod === 'postal';
    return { methodLabel, addressTypeLabel, isPostal, shinkansenAddress, shinkansenAddressee, postcode };
  }, [hasShinkansenTransport, shinkansenInfo]);

  const hasNoReservation =
    !(hasHotelReservation && hotelTravelerInfo) &&
    !(hasFlightTransport && flightTravelerInfo) &&
    !(hasShinkansenTransport && shinkansenTravelerInfo);

  if (hasNoReservation) {
    return (
      <div style={styles.collapsible}>
        <div style={styles.collapsibleHeader}>予約情報</div>
        <div style={styles.collapsibleBody}>予約情報がここに表示されます。</div>
      </div>
    );
  }

  return (
    <>
      {hasHotelReservation && hotelTravelerInfo && (
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>宿泊情報</div>
          <div style={styles.collapsibleBody}>
            <TravelerInfoDisplay travelerInfo={hotelTravelerInfo} displayType={TRAVELER_DISPLAY_TYPE.HOTEL} />
          </div>
        </div>
      )}
      {hasFlightTransport && flightTravelerInfo && (
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>航空券搭乗情報</div>
          <div style={styles.collapsibleBody}>
            <TravelerInfoDisplay travelerInfo={flightTravelerInfo} displayType={TRAVELER_DISPLAY_TYPE.FLIGHT} />
          </div>
        </div>
      )}
      {hasShinkansenTransport && shinkansenTravelerInfo && (
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>新幹線搭乗情報</div>
          <div style={styles.collapsibleBody}>
            <TravelerInfoDisplay
              travelerInfo={shinkansenTravelerInfo}
              displayType={TRAVELER_DISPLAY_TYPE.SHINKANSEN}
            />
            {shinkansenTicketingSummary && (
              <ul style={styles.list}>
                {shinkansenTicketingSummary.methodLabel && (
                  <li>発券方法: {shinkansenTicketingSummary.methodLabel}</li>
                )}
                {shinkansenTicketingSummary.isPostal && shinkansenTicketingSummary.addressTypeLabel && (
                  <li>切符送り先: {shinkansenTicketingSummary.addressTypeLabel}</li>
                )}
                {shinkansenTicketingSummary.isPostal && shinkansenTicketingSummary.postcode && (
                  <li>郵便番号: {shinkansenTicketingSummary.postcode}</li>
                )}
                {shinkansenTicketingSummary.isPostal && shinkansenTicketingSummary.shinkansenAddress && (
                  <li>送り先住所: {shinkansenTicketingSummary.shinkansenAddress}</li>
                )}
                {shinkansenTicketingSummary.isPostal && shinkansenTicketingSummary.shinkansenAddressee && (
                  <li>送り先宛名: {shinkansenTicketingSummary.shinkansenAddressee}</li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const getStyles = (theme: AITTheme) => ({
  collapsible: {
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '6px',
    marginTop: '12px'
  },
  collapsibleHeader: {
    padding: '10px 12px',
    backgroundColor: theme.contentBgColor,
    fontWeight: 600 as const
  },
  collapsibleBody: {
    padding: '12px',
    color: theme.grayTextColor,
    fontSize: '13px'
  },
  list: {
    listStyle: 'none' as const,
    padding: 0,
    margin: '12px 0 0',
    lineHeight: 1.8,
    fontSize: '13px',
    borderTop: `1px solid ${theme.grayBorderColor}`,
    paddingTop: '12px'
  }
});

export default ReservationInfoSection;
